'use client';

/**
 * useUserFeatures Hook
 * Manages user feature flag preferences with localStorage persistence
 * and optional Supabase user_metadata sync for cross-device consistency
 *
 * Feature flag states:
 * - 'disabled': Feature is blocked/hidden
 * - 'warning': Feature is enabled but shows warning badges
 * - 'enabled': Feature is fully enabled without warnings
 *
 * Usage:
 * ```tsx
 * const { flags, cycleFlag, isAccessible, setWarningAll } = useUserFeatures();
 *
 * // Check if a specific tier is accessible (warning or enabled)
 * if (isAccessible('preview_prototype')) { ... }
 *
 * // Check if should show warning badge
 * if (showsWarning('preview_prototype')) { ... }
 *
 * // Cycle through states: disabled -> warning -> enabled -> disabled
 * cycleFlag('preview_beta');
 * ```
 */

import { useState, useEffect, useCallback, useMemo } from 'react';
import { createClient } from '@/lib/supabase/client';
import logger from '@/lib/logger';
import {
  FEATURE_FLAGS_STORAGE_KEY,
  DEFAULT_FEATURE_FLAGS,
  ALL_FEATURE_FLAGS,
  FEATURE_TIERS,
  isFeatureAccessible,
  shouldShowWarningBadge,
  normalizeFeatureFlagState,
  type FeatureFlagKey,
  type FeatureFlagState,
  type FeatureTier,
} from './config';

export interface UseUserFeaturesResult {
  /** Current feature flag state */
  flags: Record<FeatureFlagKey, FeatureFlagState>;
  /** Whether flags have been loaded from storage */
  isLoaded: boolean;
  /** Whether flags are being synced to server */
  isSyncing: boolean;
  /** Cycle a flag through states: disabled -> warning -> enabled -> disabled */
  cycleFlag: (flagKey: FeatureFlagKey) => void;
  /** Set a specific flag to a specific state */
  setFlag: (flagKey: FeatureFlagKey, state: FeatureFlagState) => void;
  /** Check if a flag is accessible (warning or enabled) */
  isAccessible: (flagKey: FeatureFlagKey) => boolean;
  /** Check if a flag shows warning badges */
  showsWarning: (flagKey: FeatureFlagKey) => boolean;
  /** Check if a tier is accessible by tier name */
  isTierAccessible: (tier: FeatureTier) => boolean;
  /** Check if a tier shows warning badges */
  tierShowsWarning: (tier: FeatureTier) => boolean;
  /** Set all features to warning state (show badges) */
  setWarningAll: () => void;
  /** Enable all preview features (no warnings) */
  enableAll: () => void;
  /** Disable all preview features */
  disableAll: () => void;
  /** Force sync to Supabase (manual trigger) */
  syncToServer: () => Promise<void>;
  // Legacy compatibility
  /** @deprecated Use isAccessible instead */
  isEnabled: (flagKey: FeatureFlagKey) => boolean;
  /** @deprecated Use isTierAccessible instead */
  isTierEnabled: (tier: FeatureTier) => boolean;
  /** @deprecated Use cycleFlag instead */
  toggleFlag: (flagKey: FeatureFlagKey) => void;
}

/**
 * Load feature flags from localStorage
 * Handles migration from legacy boolean format to new tri-state format
 */
function loadFromStorage(): Record<FeatureFlagKey, FeatureFlagState> {
  if (typeof window === 'undefined') {
    return { ...DEFAULT_FEATURE_FLAGS };
  }

  try {
    const stored = localStorage.getItem(FEATURE_FLAGS_STORAGE_KEY);
    if (stored) {
      const parsed = JSON.parse(stored);
      // Normalize all values (handles legacy boolean -> tri-state migration)
      const normalized: Record<FeatureFlagKey, FeatureFlagState> = { ...DEFAULT_FEATURE_FLAGS };
      for (const key of ALL_FEATURE_FLAGS) {
        if (key in parsed) {
          normalized[key] = normalizeFeatureFlagState(parsed[key]);
        }
      }
      return normalized;
    }
  } catch (error) {
    logger.warn('useUserFeatures', 'Failed to load feature flags from localStorage', { error });
  }

  return { ...DEFAULT_FEATURE_FLAGS };
}

/**
 * Save feature flags to localStorage
 */
function saveToStorage(flags: Record<FeatureFlagKey, FeatureFlagState>): void {
  if (typeof window === 'undefined') return;

  try {
    localStorage.setItem(FEATURE_FLAGS_STORAGE_KEY, JSON.stringify(flags));
  } catch (error) {
    logger.warn('useUserFeatures', 'Failed to save feature flags to localStorage', { error });
  }
}

/**
 * Get next state in the cycle: disabled -> warning -> enabled -> disabled
 */
function getNextState(current: FeatureFlagState): FeatureFlagState {
  switch (current) {
    case 'disabled': return 'warning';
    case 'warning': return 'enabled';
    case 'enabled': return 'disabled';
    default: return 'disabled';
  }
}

/**
 * Hook for managing user feature flag preferences
 *
 * @param syncToSupabase - Whether to sync flags to Supabase user_metadata (default: false)
 */
export function useUserFeatures(syncToSupabase: boolean = false): UseUserFeaturesResult {
  const [flags, setFlags] = useState<Record<FeatureFlagKey, FeatureFlagState>>(DEFAULT_FEATURE_FLAGS);
  const [isLoaded, setIsLoaded] = useState(false);
  const [isSyncing, setIsSyncing] = useState(false);

  // Load flags from localStorage on mount
  useEffect(() => {
    const loadedFlags = loadFromStorage();
    setFlags(loadedFlags);
    setIsLoaded(true);
    logger.debug('useUserFeatures', 'Loaded feature flags', { flags: loadedFlags });
  }, []);

  // Sync to Supabase user_metadata (optional)
  const syncToServer = useCallback(async () => {
    if (!syncToSupabase) return;

    setIsSyncing(true);
    try {
      const supabase = createClient();
      const { data: { user } } = await supabase.auth.getUser();

      if (!user) {
        logger.debug('useUserFeatures', 'No user logged in, skipping server sync');
        return;
      }

      // Update user_metadata with current flags
      const { error } = await supabase.auth.updateUser({
        data: {
          ...user.user_metadata,
          feature_flags: flags,
        },
      });

      if (error) {
        throw error;
      }

      logger.debug('useUserFeatures', 'Synced feature flags to Supabase', { flags });
    } catch (error) {
      logger.error('useUserFeatures', 'Failed to sync feature flags to Supabase', { error });
    } finally {
      setIsSyncing(false);
    }
  }, [syncToSupabase, flags]);

  // Cycle a flag through states: disabled -> warning -> enabled -> disabled
  const cycleFlag = useCallback((flagKey: FeatureFlagKey) => {
    setFlags((prev) => {
      const newState = getNextState(prev[flagKey]);
      const newFlags = {
        ...prev,
        [flagKey]: newState,
      };
      saveToStorage(newFlags);
      logger.debug('useUserFeatures', 'Cycled feature flag', { flagKey, newState });
      return newFlags;
    });
  }, []);

  // Set a specific flag to a specific state
  const setFlag = useCallback((flagKey: FeatureFlagKey, state: FeatureFlagState) => {
    setFlags((prev) => {
      const newFlags = {
        ...prev,
        [flagKey]: state,
      };
      saveToStorage(newFlags);
      logger.debug('useUserFeatures', 'Set feature flag', { flagKey, state });
      return newFlags;
    });
  }, []);

  // Check if a flag is accessible (warning or enabled)
  const isAccessible = useCallback(
    (flagKey: FeatureFlagKey): boolean => {
      return isFeatureAccessible(flags[flagKey]);
    },
    [flags]
  );

  // Check if a flag shows warning badges
  const showsWarning = useCallback(
    (flagKey: FeatureFlagKey): boolean => {
      return shouldShowWarningBadge(flags[flagKey]);
    },
    [flags]
  );

  // Check if a tier is accessible by tier name
  const isTierAccessible = useCallback(
    (tier: FeatureTier): boolean => {
      const flagKey = FEATURE_TIERS[tier].flagKey;
      return isFeatureAccessible(flags[flagKey]);
    },
    [flags]
  );

  // Check if a tier shows warning badges
  const tierShowsWarning = useCallback(
    (tier: FeatureTier): boolean => {
      const flagKey = FEATURE_TIERS[tier].flagKey;
      return shouldShowWarningBadge(flags[flagKey]);
    },
    [flags]
  );

  // Set all features to warning state
  const setWarningAll = useCallback(() => {
    const allWarning = ALL_FEATURE_FLAGS.reduce(
      (acc, key) => ({ ...acc, [key]: 'warning' as FeatureFlagState }),
      {} as Record<FeatureFlagKey, FeatureFlagState>
    );
    setFlags(allWarning);
    saveToStorage(allWarning);
    logger.debug('useUserFeatures', 'Set all feature flags to warning');
  }, []);

  // Enable all preview features (no warnings)
  const enableAll = useCallback(() => {
    const allEnabled = ALL_FEATURE_FLAGS.reduce(
      (acc, key) => ({ ...acc, [key]: 'enabled' as FeatureFlagState }),
      {} as Record<FeatureFlagKey, FeatureFlagState>
    );
    setFlags(allEnabled);
    saveToStorage(allEnabled);
    logger.debug('useUserFeatures', 'Enabled all feature flags');
  }, []);

  // Disable all preview features
  const disableAll = useCallback(() => {
    const allDisabled = { ...DEFAULT_FEATURE_FLAGS };
    setFlags(allDisabled);
    saveToStorage(allDisabled);
    logger.debug('useUserFeatures', 'Disabled all feature flags');
  }, []);

  // Legacy compatibility wrappers
  const isEnabled = isAccessible; // deprecated alias
  const isTierEnabled = isTierAccessible; // deprecated alias
  const toggleFlag = cycleFlag; // deprecated alias

  // Memoize the result to prevent unnecessary rerenders
  const result = useMemo<UseUserFeaturesResult>(
    () => ({
      flags,
      isLoaded,
      isSyncing,
      cycleFlag,
      setFlag,
      isAccessible,
      showsWarning,
      isTierAccessible,
      tierShowsWarning,
      setWarningAll,
      enableAll,
      disableAll,
      syncToServer,
      // Legacy compatibility
      isEnabled,
      isTierEnabled,
      toggleFlag,
    }),
    [flags, isLoaded, isSyncing, cycleFlag, setFlag, isAccessible, showsWarning, isTierAccessible, tierShowsWarning, setWarningAll, enableAll, disableAll, syncToServer, isEnabled, isTierEnabled, toggleFlag]
  );

  return result;
}

export default useUserFeatures;
